import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ACCENT } from "../artistStudio/shared";
import { fanHomeApi, isSignedIn, greetingFor, storedFirstName } from "./fanHomeApi";
import { usePlayer, PlayPauseIcon } from "./PlayerContext";
import TrackCard from "./TrackCard";
import MusicShell from "./MusicShell";
import { useScrollMemory } from "./scrollMemory";
import { useSection, LazyMount, SectionFrame, SkeletonGrid, SectionError, EmptyNote, CARD_GRID, CARD_ROW } from "./Section";

// Fan Home Page: greeting, Continue Playing, New from artists you follow, sponsored banner, Recommended, Promoted
// tracks and Trending Now. Each section loads on its own; the ones below the fold load when they come near the screen.

function Greeting({ signedIn }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000); // the greeting follows the local clock
    return () => clearInterval(timer);
  }, []);
  const name = signedIn ? storedFirstName() : "";
  return (
    <h1 className="text-3xl font-bold text-gray-900">{greetingFor(now)}{name ? `, ${name}` : ""}</h1>
  );
}

// layout "grid": four cards across; layout "row": a sideways strip of fixed-width cards.
function TrackSection({ id, title, subtitle, section, empty, render, layout = "grid" }) {
  const { status, data, error, reload } = section;
  const row = layout === "row";
  let body;
  if (status === "loading" && !data) body = <SkeletonGrid row={row} count={row ? 4 : 4} />;
  else if (status === "error" && !data) body = <SectionError message={error} onRetry={reload} />;
  else if (!data?.items?.length) body = empty;
  else body = <div className={row ? CARD_ROW : CARD_GRID}>{data.items.map(render)}</div>;
  return <SectionFrame id={id} title={title} subtitle={typeof subtitle === "function" ? subtitle(data) : subtitle}>{body}</SectionFrame>;
}

function ContinuePlaying() {
  const section = useSection(fanHomeApi.continuePlaying, { autoRefresh: true });
  return (
    <TrackSection
      id="continue-playing" title="Continue playing" section={section} layout="row"
      empty={<EmptyNote>Nothing to pick up yet. Play something below and it will appear here.</EmptyNote>}
      render={(t, _i, all) => <TrackCard key={t.id} track={t} variant="compact" showProgress queue={all} />}
    />
  );
}

function NewFromFollowed() {
  const section = useSection(fanHomeApi.newReleases, { autoRefresh: true });
  const following = section.data?.following_count;
  return (
    <TrackSection
      id="new-from-followed" title="New from artists you follow" section={section} layout="row"
      empty={<EmptyNote>{following === 0 ? "Follow artists to see their new releases here." : "No new releases from the artists you follow right now."}</EmptyNote>}
      render={(t, _i, all) => <TrackCard key={t.id} track={t} variant="compact" showReleased queue={all} />}
    />
  );
}

// Counts a sponsored card as shown once at least half of it is on screen, and a click only after that.
function useAdTracking(ad) {
  const ref = useRef(null);
  const tracking = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!ad || !el) return undefined;
    const record = () => {
      if (!tracking.current) {
        tracking.current = fanHomeApi.adImpression({
          auction_id: ad.auction_id, ad_id: ad.ad_id, ad_set_id: ad.ad_set_id, campaign_id: ad.campaign_id, placement: ad.placement,
        }).catch(() => null);
      }
    };
    if (typeof IntersectionObserver === "undefined") { record(); return undefined; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.5)) { record(); observer.disconnect(); }
    }, { threshold: [0.5] });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ad]);
  const click = useCallback(async () => {
    if (!ad) return;
    if (!tracking.current) {
      tracking.current = fanHomeApi.adImpression({
        auction_id: ad.auction_id, ad_id: ad.ad_id, ad_set_id: ad.ad_set_id, campaign_id: ad.campaign_id, placement: ad.placement,
      }).catch(() => null);
    }
    const result = await tracking.current;
    if (result?.tracking_id) fanHomeApi.adClick({ tracking_id: result.tracking_id }).catch(() => {});
  }, [ad]);
  return { ref, click };
}

function SponsoredBanner({ onSettled }) {
  const { play } = usePlayer();
  const section = useSection(() => fanHomeApi.banner({ device_type: "web" }));
  const ad = section.data?.ad || null;
  const { ref, click } = useAdTracking(ad);
  useEffect(() => {
    if (section.status === "ready") onSettled(ad);
    if (section.status === "error") onSettled(null);
  }, [section.status, ad, onSettled]);
  if (!ad) return null; // no eligible campaign (or the ad service failed): the page simply has no banner
  const listen = () => { click(); play(ad.track, { source: "promoted" }); };
  return (
    <section ref={ref} aria-label="Sponsored" className="mt-10 flex items-center gap-4 rounded-2xl bg-[#e8dec6] px-5 py-4">
      <button type="button" onClick={listen} aria-label={`Play ${ad.track.title}`} className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-[#f3ecda]">
        {(ad.image_url || ad.track.cover_url) && <img src={ad.image_url || ad.track.cover_url} alt="" className="h-full w-full object-cover" />}
        <span className="absolute left-1/2 top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-white shadow" style={{ backgroundColor: ACCENT }}><span className="scale-[0.6]"><PlayPauseIcon playing={false} /></span></span>
      </button>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-xs text-gray-600">
          <span className="rounded bg-gray-600 px-1.5 py-px text-[10px] font-semibold text-white">{ad.label}</span>
          <span className="sm:truncate">Promoted track{ad.advertiser_name ? ` · ${ad.advertiser_name}` : ""}</span>
        </p>
        <p className="mt-1 text-[15px] font-bold text-gray-900 sm:truncate">{ad.headline}</p>
        {ad.body_text && <p className="text-xs text-gray-600 sm:truncate">{ad.body_text}</p>}
      </div>
      <button type="button" onClick={listen} className="shrink-0 rounded-full bg-white px-5 py-2 text-xs font-semibold text-gray-900">{ad.cta_label || "Listen Now"}</button>
    </section>
  );
}

function PromotedItem({ ad }) {
  const { ref, click } = useAdTracking(ad);
  return (
    <div ref={ref}>
      <TrackCard track={ad.track} badge="Promoted" showPlays showReleased onPlay={click} source="promoted" />
    </div>
  );
}

function PromotedTracks({ banner }) {
  // Mounted only after the banner has settled, so the same sponsored card is not shown twice on the page.
  const section = useSection(() => fanHomeApi.promoted({
    device_type: "web",
    ...(banner.ad ? { exclude_ad_ids: String(banner.ad.ad_id), exclude_track_ids: String(banner.ad.track.id) } : {}),
  }));
  const items = section.data?.items || [];
  if (section.status === "ready" && !items.length) return null;
  if (section.status === "error") return null;
  return (
    <section aria-label="Promoted tracks" className="mt-4">
      {section.status === "loading" && !items.length ? <SkeletonGrid count={3} /> : (
        <div className={CARD_GRID}>{items.map((ad) => <PromotedItem key={ad.ad_id} ad={ad} />)}</div>
      )}
    </section>
  );
}

function Recommended() {
  const section = useSection(fanHomeApi.recommended, { autoRefresh: true });
  return (
    <TrackSection
      id="recommended" title="Recommended for you" section={section}
      subtitle={(d) => (d?.fallback ? "Popular on Nyawoo right now. Play and follow artists to get picks made for you." : undefined)}
      empty={<EmptyNote>No tracks to recommend yet. Check back soon.</EmptyNote>}
      render={(t, _i, all) => <TrackCard key={t.id} track={t} showPlays showReleased note={t.reason} queue={all} />}
    />
  );
}

function Trending() {
  const section = useSection(fanHomeApi.trending, { autoRefresh: true });
  return (
    <TrackSection
      id="trending-now" title="Trending now" section={section}
      subtitle={<Link to="/fan/charts" className="font-semibold hover:underline" style={{ color: ACCENT }}>See all charts</Link>}
      empty={<EmptyNote>Trending tracks will show up here once people start listening.</EmptyNote>}
      render={(t, _i, all) => <TrackCard key={t.id} track={t} showPlays queue={all} />}
    />
  );
}

export default function FanHome() {
  useScrollMemory("fan-home"); // coming back from a sound page puts the fan where they were
  const signedIn = isSignedIn();
  const [banner, setBanner] = useState({ settled: !signedIn, ad: null });
  const onBannerSettled = useCallback((ad) => setBanner((prev) => (prev.settled && prev.ad === ad ? prev : { settled: true, ad })), []);

  return (
    <MusicShell>
        <Greeting signedIn={signedIn} />
        {signedIn ? (
          <>
            <ContinuePlaying />
            <NewFromFollowed />
            <SponsoredBanner onSettled={onBannerSettled} />
          </>
        ) : (
          <p className="mt-6 rounded-2xl bg-green-50 px-5 py-4 text-gray-800">
            <Link to="/login" className="font-semibold underline" style={{ color: ACCENT }}>Log in</Link> to see what you were playing, new music from artists you follow, and picks made for you.
          </p>
        )}
        <LazyMount><Recommended /></LazyMount>
        {signedIn && banner.settled && <LazyMount minHeight={120}><PromotedTracks banner={banner} /></LazyMount>}
        <LazyMount><Trending /></LazyMount>
    </MusicShell>
  );
}
